var HeaderVariables = require("app/theme/styles/LargeMediaHeaderVariables"),
    Layout          = require("app/ui/Layout"),
    appThemeVars    = require("app/theme/variables.js"),
    dom             = require("enyo/dom"),
    kind            = require("enyo/kind");

module.exports = kind(/** @lends  app.ui.LargeMediaHeaderLayout.prototype */ {

    /**
     * @private
     */
    name: "app.ui.LargeMediaHeaderLayout",

    /**
     * @private
     */
    kind: Layout,

    /**
     * Default options for the layout.
     *
     * @type {Object}
     * @protected
     */
    options: {

        /**
         * Generally the LargeMediaHeader should expand to fill the AppLayout's
         * Content Region. Some media items will have a dashboard with streams
         * below the LargeMediaHeader though. In those cases, we want to reduce
         * the minimum height to allow a peek at the dashboard.
         *
         * @return   {boolean}  Whether to allow a peek at the dashboard
         * @public
         */
        peekAtDashboard: function() {
            return this.container.get("peekAtDashboard") || false;
        },

        /**
         * The height of the peek we want to give at the dashboard.
         *
         * @type    {number|function}
         * @return  {number}  The height of the peek
         * @public
         */
        dashboardPeekHeight: function() {
            // The height of the title bar of a carouselRow or gridRow plus some
            // padding above and some cover content below.
            return appThemeVars.remToPx
                + appThemeVars.getContentPaddingVerticalPx()
                + appThemeVars.getHeaderLineHeightPx("h2")
                + appThemeVars.getContentPaddingVerticalPx()
                + appThemeVars.remToPx * 3;
        }
    },

    /**
     * @private
     */
    metrics: {},

    /**
     * @private
     */
    reflow: function() {
        this.metrics = {};
        this.reflowContainer();
        this.reflowCoverArea();
        this.reflowActions();
        this.reflowTabs();
        this.reflowBackground();
    },

    /**
     * @private
     */
    reflowContainer: function() {
        var $container          = this.container,
            $appView            = $container.app.view,
            headerRegionBounds  = $appView.get("headerRegionBounds") || {},
            contentRegionBounds = $appView.get("contentRegionBounds") || {},
            paddingTop,
            width,
            minHeight;

        // Optionally make space for the app header
        paddingTop = appThemeVars.appHeaderFloat
            ? headerRegionBounds.height || 0
            : 0;

        // Scale to cover the AppLayout's Content Region
        width = contentRegionBounds.width || 0;

        // Mobile & Medium layout let content dictate height
        if (appThemeVars.windowWidth < HeaderVariables.getLargeLayoutBreakpointPx()) {
            minHeight = 0;
        }

        // Large layout tries to fill the screen
        else {
            minHeight = contentRegionBounds.height || 0;

            // Optionally reduce the minimum height a little to give the user a
            // peek at the Dashboard below
            if (this.getOption("peekAtDashboard")) {
                minHeight -= this.getOption("dashboardPeekHeight") || 0;
            }
        }

        $container.addStyles({
            paddingTop: paddingTop + "px",
            width:      width + "px",
            minHeight:  minHeight + "px"
        });

        this.metrics.containerPaddingTop = paddingTop;
        this.metrics.containerWidth      = width;
        this.metrics.containerMinHeight  = minHeight;
        this.metrics.contentRegionHeight = contentRegionBounds.height || 0;
    },

    /**
     * @private
     */
    reflowCoverArea: function() {
        var $container      = this.container,
            $coverArea      = $container.$.coverArea,
            $coverContainer = $container.$.coverContainer,
            $cover          = $container.$.cover,
            paddingTop,
            paddingBottom,
            maxCoverWidth,
            maxCoverHeight,
            coverAspectRatio,
            coverSize;

        // Space around the cover, revealing background
        paddingTop    = HeaderVariables.getCoverAreaPaddingTopPx();
        paddingBottom = HeaderVariables.getCoverAreaPaddingBottomPx();

        $coverArea.addStyles({
            paddingTop:    paddingTop + "px",
            paddingBottom: paddingBottom + "px"
        });

        this.metrics.coverAreaPaddingTop    = paddingTop;
        this.metrics.coverAreaPaddingBottom = paddingBottom;

        // Calculate the maximum space available to the Cover and derive the
        // maximum Cover size from that given it's aspect ratio
        maxCoverWidth    = HeaderVariables.getCoverMaxWidthPx(this.metrics);
        maxCoverHeight   = HeaderVariables.getCoverMaxHeightPx(this.metrics);
        coverAspectRatio = ($cover.get("rawImageSrc") || {}).ratio || 1;
        coverSize        = this.calcMaxFittingCover(
            maxCoverWidth,
            maxCoverHeight,
            coverAspectRatio
        );

        $coverContainer.addStyles({
            width:  coverSize.width + "px",
            height: coverSize.height + "px"
        });

        this.metrics.coverWidth  = coverSize.width;
        this.metrics.coverHeight = coverSize.height;
    },

    /**
     * @private
     */
    reflowActions: function() {
        var $container       = this.container,
            $actions         = $container.$.actions,
            $bookmarkButton1 = $container.$.bookmarkButton1,
            $bookmarkButton2 = $container.$.bookmarkButton2,
            $followButton    = $container.$.followButton,
            $shareButton     = $container.$.shareButton,
            actionsNode      = $actions.hasNode(),
            actionsWidth,
            actionsPaddingLeft,
            actionsPaddingRight,
            showBigBookmarkButton;

        actionsWidth        = actionsNode
            ? parseFloat(dom.getComputedStyleValue(actionsNode, "width"), 10)
            : 0;
        actionsPaddingLeft  = actionsNode
            ? parseFloat(dom.getComputedStyleValue(actionsNode, "padding-left"), 10)
            : 0;
        actionsPaddingRight = actionsNode
            ? parseFloat(dom.getComputedStyleValue(actionsNode, "padding-right"), 10)
            : 0;

        showBigBookmarkButton = actionsNode && (
            actionsWidth - actionsPaddingLeft - actionsPaddingRight
            > HeaderVariables.getLargeBookmarkButtonMinSpacePx()
        );

        if (showBigBookmarkButton) {
            // Hide small bookmark button
            if ($bookmarkButton2.hasNode()) {
                $bookmarkButton2.removeNodeFromDom();
                $bookmarkButton2.teardownRender();
            }
            $bookmarkButton2.set("canGenerate", false);

            // Show large bookmark button
            $bookmarkButton1.set("canGenerate", true);
            !$bookmarkButton1.hasNode() && $bookmarkButton1.render();
        }
        else {
            // Hide large bookmark button
            if ($bookmarkButton1.hasNode()) {
                $bookmarkButton1.removeNodeFromDom();
                $bookmarkButton1.teardownRender();
            }
            $bookmarkButton1.set("canGenerate", false);

            // Show small bookmark button
            $bookmarkButton2.set("canGenerate", true);
            !$bookmarkButton2.hasNode() && $bookmarkButton2.render();
        }

        if (HeaderVariables.showSmallButtonCaptions()) {
            $bookmarkButton2.set("showCaption", true);
            $followButton.set("showCaption", true);
            $shareButton.set("showCaption", true);
        }
        else {
            $bookmarkButton2.set("showCaption", false);
            $followButton.set("showCaption", false);
            $shareButton.set("showCaption", false);
        }
    },

    /**
     * @private
     */
    reflowTabs: function() {
        var $container = this.container,
            $info      = $container.$.info,
            $credits   = $container.$.credits,
            activeTab  = $container.get("activeTab");

        if (appThemeVars.windowWidth < HeaderVariables.getMediumLayoutBreakpointPx()
            || appThemeVars.windowWidth >= HeaderVariables.getLargeLayoutBreakpointPx()) {
            $info.set("showing", $info.get("tab") === activeTab);
            $credits.set("showing", $credits.get("tab") === activeTab);
        }
        else {
            $info.show();
            $credits.show();
        }
    },

    /**
     * @private
     */
    reflowBackground: function() {
        var $container         = this.container,
            $appView           = $container.app.view,
            $background        = $container.$.background,
            headerRegionBounds = $appView.get("headerRegionBounds") || {},
            containerBounds,
            width,
            height;

        width  = this.metrics.containerWidth || 0;
        height = HeaderVariables.getBackgroundHeightPx(this.metrics);

        height += appThemeVars.appHeaderFloat
            ? headerRegionBounds.height || 0
            : 0;

        containerBounds = $container.getBounds();
        height          = Math.min(height, containerBounds.height);

        $background.addStyles({
            width:  width + "px",
            height: height + "px"
        });

        this.metrics.backgroundWidth  = width;
        this.metrics.backgroundHeight = height;
    },

    /**
     * @param   {number}  maxWidth     The maximum width of the cover
     * @param   {number}  maxHeight    The maximum height of the cover
     * @param   {number}  aspectRatio  The aspect ratio of the cover
     * @return  {Object}  The largest width and height that fit within the
     *                    bounds given the provided aspect ratio.
     * @private
     */
    calcMaxFittingCover: function(maxWidth, maxHeight, aspectRatio) {
        var width,
            height;

        width  = maxWidth;
        height = width / aspectRatio;

        if (height > maxHeight) {
            height = maxHeight;
            width  = height * aspectRatio;
        }

        return {
            width:  width,
            height: height
        };
    }

});
